
import EoSlider from '../EoSlider/EoSlider'

export default {
  name: 'EoAudioPlayer',
  components: {
    EoSlider,
  },
  props: {
    /**
     * Force a playing state
     * @todo: change into a v-model
     */
    playing: {
      type: Boolean,
      default: false,
    },
    /**
     * Force the player to jump to specific timestamp
     */
    jumpTo: {
      type: [String, Number],
      default: null,
    },
    /**
     * Source file of the audio
     */
    src: {
      type: String,
      default: null,
    },
    /**
     * Preload the file of the audio
     */
    preload: {
      type: Boolean,
      default: true,
    },
    /**
     * Autoplay the track
     */
    autoplay: {
      type: Boolean,
      default: false,
    },
    /**
     * Loop the audio when finished
     */
    loop: {
      type: Boolean,
      default: false,
    },
    /**
     * Title
     */
    title: {
      type: String,
      default: '',
    },
    /**
     * Style: background color
     */
    color: {
      type: String,
      default: 'grey-800',
    },
    /**
     * Style: color of the interactive elements
     */
    accentColor: {
      type: String,
      default: 'primary',
    },
  },
  data () {
    return {
      isPlaying: false, // Only the HTML Audio element should change this state
      isLoaded: false,
      isSeeking: false,
      isPausedBySeeking: false,
      currentTime: 0,
      duration: 0,
    }
  },
  watch: {
    /**
     * Watch if the audio source gets updated
     */
    src () {
      this.isLoaded = false
      this.setPlayState(false)
      // this.isPlaying = false

      /**
       * Emitted when the source has changed
       * @event source
       */
      this.$emit('source')
    },
    /**
     * Watch the prop and sync component state with it
     */
    jumpTo (value) {
      if (value !== null) {
        this.isSeeking = true
        this.onSeekerTimeJump(Number(value))
      }
    },
    /**
     * Watch the `playing` prop and overwrite intended play state
     */
    playing (shouldPlay) {
      this.setPlayState(shouldPlay)
    },
    isSeeking (isSeeking) {
      /**
       * Emit event when user is interacting with the slider
       * @event seeking
       */
      this.$emit('seeking', isSeeking)
    },
  },
  mounted () {
    if (this.$refs.htmlAudio) {
      this.$refs.htmlAudio.addEventListener('loadeddata', this.onAudioLoaded, false)
      this.$refs.htmlAudio.addEventListener('loadedmetadata', this.onAudioLoaded, false) // iOS fallback
      this.$refs.htmlAudio.addEventListener('progress', this.onAudioLoaded, { once: true }) // iOS fallback
      this.$refs.htmlAudio.addEventListener('canplaythrough', this.onAudioLoaded, false) // iOS fallback
      this.$refs.htmlAudio.addEventListener('play', this.onAudioPlay, false)
      this.$refs.htmlAudio.addEventListener('timeupdate', this.onAudioTimeUpdate, false)
      this.$refs.htmlAudio.addEventListener('pause', this.onAudioPause, false)
    }
  },
  methods: {
    formatCurrentTime (secondsPassed) {
      if (this.duration === 0) {
        return '--:--'
      } else {
        const secondsRemaining = Math.floor(this.duration - secondsPassed)
        const mins = Math.floor(secondsRemaining / 60)
        const secs = Math.floor(secondsRemaining % 60)
        return `-${mins < 10 ? '0' : ''}${mins}:${secs < 10 ? '0' : ''}${secs}`
      }
    },
    onSeekerTouchStart () {
      this.isSeeking = true
      if (this.isPlaying) {
        this.setPlayState(false)
        this.isPausedBySeeking = true
      }
    },
    onSeekerTimeJump (value) {
      if (this.isSeeking) {
        this.$refs.htmlAudio.currentTime = value
        if (this.isPausedBySeeking) {
          this.setPlayState(true)
          this.isPausedBySeeking = false
        }
        this.isSeeking = false
      }
    },
    onAudioLoaded () {
      if (this.$refs.htmlAudio.readyState >= 2) {
        this.isLoaded = true
        /**
         * Emitted when the source has been loaded
         * @event load
         */
        this.$emit('load')

        this.currentTime = this.jumpTo || this.$refs.htmlAudio.currentTime

        if (!isNaN(this.$refs.htmlAudio.duration)) {
          this.duration = this.$refs.htmlAudio.duration
        }

        const shouldPlay = this.isPlaying || this.autoplay
        this.setPlayState(shouldPlay)
      } else {
        this.$log?.warn('Failed to load audiofile')
      }
    },
    onAudioPlay () {
      this.isPlaying = true
      /**
       * Emitted when the player starts playing
       * @event play
       */
      this.$emit('play')
    },
    onAudioTimeUpdate () {
      if (!this.isSeeking) {
        this.currentTime = this.$refs.htmlAudio.currentTime

        if (!isNaN(this.$refs.htmlAudio.duration)) {
          this.duration = this.$refs.htmlAudio.duration
        }

        /**
         * Emitted while audio progresses playing
         * @event source
         */
        this.$emit('progress', this.$refs.htmlAudio.currentTime)
      }
    },
    onAudioPause () {
      this.isPlaying = false
      /**
       * Emitted when the player is paused
       * @event play
       */
      this.$emit('pause')
    },
    setPlayState (shouldPlay) {
      if (!this.src) return
      try {
        if (shouldPlay) {
          this.$refs.htmlAudio.play()
        } else {
          this.$refs.htmlAudio.pause()
        }
      } catch (error) {
        this.$log?.warn(`Failed to ${shouldPlay ? 'start' : 'stop'} playing audiofile`)
      }
    },
  },
}
